import { useState, useEffect, useCallback } from "react";
import {
  FaEdit,
  FaTimes,
  FaCheck,
  FaPlus,
  FaTicketAlt,
  FaUsers,
  FaTag,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { useApi } from "../context/useApi";
import { useAuth } from "../context/AuthContext";
const API_BASE = "https://admin-backend-fbuuh6fo3q-el.a.run.app/marketing";


export default function MarketingPanel() {
  const { adminId } = useAuth();
    const { apiFetch } = useApi();
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
      const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast({ message: "", type: "" }), 3000);
      };
const [newForm, setNewForm] = useState({
  created_for: "",
  active_status: true,
  tags: [], 
  tagInput: "", 
  remarks: "",
});

  const [toast, setToast] = useState({ message: "", type: "" });

  const fetchReferrals = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/get_referrals`);
      const data = await res.json();
      setReferrals(data);
    } catch (err) {
      console.error(err);
      showToast("Error fetching referrals", "error");
    } finally {
      setIsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

const handleEdit = (ref) => {
  setEditing(ref.referral_id);
  setEditForm({
    remarks: ref.remarks || "",
    tags: ref.tags || [], 
    active_status: ref.active_status,
    tagInput: "", 
  });
};


  const handleCancel = () => {
    setEditing(null);
    setEditForm({});
  };

const handleSave = async (id) => {
  setLoadingSave(true);
  try {
    const payload = {
      remarks: editForm.remarks,
      tags: editForm.tags || [], // use array directly
      active_status: editForm.active_status,
    };

    const res = await apiFetch(`${API_BASE}/update_referral/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (res.ok) {
      showToast("Referral updated successfully!");
      fetchReferrals();
      setEditing(null);
    } else {
      showToast(data.error || "Failed to update referral", "error");
    }
  } catch (err) {
    console.error(err);
    showToast("Error updating referral", "error");
  } finally {
    setLoadingSave(false);
  }
};

const handleAddReferral = async () => {
  if (!newForm.created_for.trim()) {
    showToast("Please enter 'Created For' field", "error");
    return;
  }

  setLoadingAdd(true);
  try {
    const payload = {
      created_by: adminId,
      created_for: newForm.created_for,
      active_status: newForm.active_status,
      tags: newForm.tags || [], 
      remarks: newForm.remarks,
    };

    const res = await apiFetch(`${API_BASE}/add_referral`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (res.ok) {
      showToast("Referral added successfully!");
      fetchReferrals();
      setShowAddModal(false);
      setNewForm({
        created_for: "",
        active_status: true,
        tags: [],
        tagInput: "",
        remarks: "",
      });
    } else {
      showToast(data.error || "Failed to add referral", "error");
    }
  } catch (err) {
    console.error(err); // optional: see exact error
    showToast("Error adding referral", "error");
  } finally {
    setLoadingAdd(false);
  }
};

  const addTag = (form, setForm, isNew = false) => {
    const tagInput = isNew ? newForm.tagInput : editForm.tagInput;
    if (tagInput?.trim()) {
      setForm({
        ...form,
        tags: [...(form.tags || []), tagInput.trim()],
        tagInput: "",
      });
    }
  };

  const removeTag = (form, setForm, index) => {
    setForm({
      ...form,
      tags: form.tags.filter((_, i) => i !== index),
    });
  };

  // Filter referrals
  const filteredReferrals = referrals.filter((ref) => {
    const matchesSearch =
      ref.referral_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.created_for.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && ref.active_status) ||
      (filterStatus === "inactive" && !ref.active_status);

    return matchesSearch && matchesStatus;
  });

  const totalReferrals = referrals.reduce(
    (sum, r) => sum + (r.no_of_referrals || 0),
    0
  );
  const activeCount = referrals.filter((r) => r.active_status).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Marketing & Referrals
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage referral codes and track campaign performance
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium"
          >
            <FaPlus size={12} />
            Add Referral
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <FaTicketAlt className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {referrals.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <FaToggleOn className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {activeCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FaUsers className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold text-gray-900">
                  {totalReferrals}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by code, campaign, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Referrals List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="border-2 border-gray-200 rounded-lg p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border-2 border-gray-300 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-5 w-40 border border-gray-300 rounded"></div>
                    <div className="h-4 w-56 border border-gray-300 rounded"></div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-6 w-16 border border-gray-300 rounded-full"></div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 border border-gray-300 rounded"></div>
                    <div className="h-8 w-8 border border-gray-300 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredReferrals.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
          <div className="inline-block p-4 border-2 border-gray-300 rounded-full mb-4">
            <FaTicketAlt className="text-gray-400" size={48} />
          </div>
          <p className="text-gray-600 font-medium mb-1">No referrals found</p>
          <p className="text-sm text-gray-500">
            {searchTerm
              ? "Try a different search term"
              : "Start by adding a new referral"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReferrals.map((ref) => {
            const isEditing = editing === ref.referral_id;

            return (
              <div
                key={ref.referral_id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                          <FaTicketAlt className="text-white" size={16} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {ref.referral_code}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {ref.created_for}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {isEditing ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.active_status}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                active_status: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${
                            ref.active_status
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ref.active_status ? (
                            <FaToggleOn size={12} />
                          ) : (
                            <FaToggleOff size={12} />
                          )}
                          {ref.active_status ? "Active" : "Inactive"}
                        </span>
                      )}

                      {!isEditing && (
                        <button
                          onClick={() => handleEdit(ref)}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                        >
                          <FaEdit size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Tags Section */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        <FaTag className="inline mr-1" size={10} /> Tags
                      </label>
                      {isEditing ? (
                        <div>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Add tag"
                              value={editForm.tagInput || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  tagInput: e.target.value,
                                })
                              }
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addTag(editForm, setEditForm, false);
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                              onClick={() =>
                                addTag(editForm, setEditForm, false)
                              }
                              className="px-3 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800"
                            >
                              Add
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {editForm.tags?.length ? (
                              editForm.tags.map((tag, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                                >
                                  {tag}
                                  <button
                                    onClick={() =>
                                      removeTag(editForm, setEditForm, i)
                                    }
                                    className="text-gray-500 hover:text-red-600"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400 text-xs">
                                No tags
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {ref.tags?.length ? (
                            ref.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No tags
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Referral Count */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        <FaUsers className="inline mr-1" size={10} /> Total
                        Referrals
                      </label>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-gray-900">
                          {ref.no_of_referrals}
                        </span>
                        <span className="text-sm text-gray-500">users</span>
                      </div>
                    </div>
                  </div>

                  {/* Remarks Section */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">
                      Remarks
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editForm.remarks}
                        onChange={(e) =>
                          setEditForm({ ...editForm, remarks: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        rows={2}
                      />
                    ) : (
                      <p className="text-sm text-gray-900">
                        {ref.remarks || "No remarks"}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {isEditing && (
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                      <button
                        onClick={handleCancel}
                        disabled={loadingSave}
                        className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSave(ref.referral_id)}
                        disabled={loadingSave}
                        className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                      >
                        <FaCheck size={12} />
                        {loadingSave ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Referral Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Referral
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Summer Campaign 2024"
                  value={newForm.created_for}
                  onChange={(e) =>
                    setNewForm({ ...newForm, created_for: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Add tag"
                    value={newForm.tagInput || ""}
                    onChange={(e) =>
                      setNewForm({ ...newForm, tagInput: e.target.value })
                    }
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag(newForm, setNewForm, true);
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => addTag(newForm, setNewForm, true)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {newForm.tags?.map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(newForm, setNewForm, i)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <FaTimes size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Remarks
                </label>
                <textarea
                  placeholder="Add any notes or remarks..."
                  value={newForm.remarks}
                  onChange={(e) =>
                    setNewForm({ ...newForm, remarks: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newForm.active_status}
                    onChange={(e) =>
                      setNewForm({
                        ...newForm,
                        active_status: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  <span className="text-sm font-medium text-gray-700">
                    {newForm.active_status ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={loadingAdd}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddReferral}
                  disabled={loadingAdd}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                >
                  <FaCheck size={12} />
                  {loadingAdd ? "Adding..." : "Add Referral"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

